import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, arrayUnion, query } from 'firebase/firestore';
import { db } from '../firebase.js';
import useFirebaseCache from '../hooks/useFirebaseCache.js';

// Create the context
const MenuContext = createContext(null);

// Custom hook to use the menu context
export const useMenus = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenus must be used within a MenuProvider');
  }
  return context;
};

// Provider component
export const MenuProvider = ({ children, user }) => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { 
    shouldRefreshCache, 
    hasCollectionChanged,
    getCachedData,
    setCachedData
  } = useFirebaseCache();

  // Load user menus with caching
  const loadUserMenus = useCallback(async () => {
    if (!user) {
      setMenus([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const cachedMenus = getCachedData(`userMenus_${user.uid}`, []);
    const cachedTimestamp = parseInt(localStorage.getItem(`userMenus_${user.uid}_timestamp`) || '0', 10);
    const needsRefresh = shouldRefreshCache(`userMenus_${user.uid}`, 10); // 10 minute cache
    
    try {
      const shouldFetch = !cachedMenus.length || 
        (needsRefresh && await hasCollectionChanged(`users/${user.uid}/menus`, cachedTimestamp));
      
      if (!shouldFetch) {
        console.log('Using cached menus - unchanged since last fetch');
        setMenus(cachedMenus);
        setLoading(false);
        return;
      }

      console.log('Fetching fresh menus');
      // Only select the fields we need
      const menusQuery = query(
        collection(db, `users/${user.uid}/menus`)
      );
      const querySnapshot = await getDocs(menusQuery);
      
      const menusData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const normalizedRecipes = (Array.isArray(data.recipes) ? data.recipes : []).map(recipeId => {
          if (typeof recipeId === 'string' && recipeId.includes('/')) {
            const parts = recipeId.split('/');
            return parts[parts.length - 1];
          }
          return String(recipeId);
        });
        return { id: doc.id, ...data, recipes: normalizedRecipes };
      });
      
      setMenus(menusData);
      setCachedData(`userMenus_${user.uid}`, menusData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading menus:', error);
      if (cachedMenus.length) {
        setMenus(cachedMenus);
      }
      setLoading(false);
    }
  }, [user, getCachedData, hasCollectionChanged, setCachedData, shouldRefreshCache]);

  // Create a new menu
  const createMenu = useCallback(async (menuData) => {
    if (!user) return;
    
    try {
      const menuRef = doc(collection(db, `users/${user.uid}/menus`));
      const newMenu = {
        ...menuData,
        id: menuRef.id,
        recipes: menuData.recipes || [],
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      
      await setDoc(menuRef, newMenu);
      
      setMenus(prevMenus => [...prevMenus, newMenu]);
      
      // Update cache
      setCachedData(`userMenus_${user.uid}`, [...menus, newMenu]);
      
      return newMenu;
    } catch (error) {
      console.error('Error creating menu:', error);
      return null;
    }
  }, [menus, setCachedData, user]);

  // Add a recipe to a menu
  const addRecipeToMenu = useCallback(async (menuId, recipeId) => {
    if (!user) return;
    
    try {
      const menuRef = doc(db, `users/${user.uid}/menus/${menuId}`);
      await updateDoc(menuRef, {
        recipes: arrayUnion(recipeId),
        updated: new Date().toISOString()
      });
      
      setMenus(prevMenus =>
        prevMenus.map(menu =>
          menu.id === menuId ? { ...menu, recipes: [...menu.recipes, recipeId] } : menu
        )
      );
      
      // Update cache
      const updatedMenus = menus.map(menu =>
        menu.id === menuId ? { ...menu, recipes: [...menu.recipes, recipeId] } : menu
      );
      setCachedData(`userMenus_${user.uid}`, updatedMenus);
      
      return true;
    } catch (error) {
      console.error('Error adding recipe to menu:', error);
      return false;
    }
  }, [menus, setCachedData, user]);

  // Remove a recipe from a menu
  const removeRecipeFromMenu = useCallback(async (menuId, recipeId) => {
    if (!user) return;
    
    try {
      const menuRef = doc(db, `users/${user.uid}/menus/${menuId}`);
      const menuDoc = await getDoc(menuRef);
      
      if (menuDoc.exists()) {
        const menu = menuDoc.data();
        const currentRecipes = Array.isArray(menu.recipes) ? menu.recipes : [];
        const updatedRecipes = currentRecipes.filter(id => id !== recipeId);
        
        await updateDoc(menuRef, {
          recipes: updatedRecipes,
          updated: new Date().toISOString()
        });
        
        setMenus(prevMenus =>
          prevMenus.map(menu =>
            menu.id === menuId ? { ...menu, recipes: updatedRecipes } : menu
          )
        );
        
        // Update cache
        const updatedMenus = menus.map(menu =>
          menu.id === menuId ? { ...menu, recipes: updatedRecipes } : menu
        );
        setCachedData(`userMenus_${user.uid}`, updatedMenus);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing recipe from menu:', error);
      return false;
    }
  }, [menus, setCachedData, user]);

  // Delete a menu
  const deleteMenu = useCallback(async (menuId) => {
    if (!user) return;
    
    try {
      await deleteDoc(doc(db, `users/${user.uid}/menus/${menuId}`));
      
      setMenus(prevMenus => prevMenus.filter(m => m.id !== menuId));
      
      // Update cache
      const updatedMenus = menus.filter(m => m.id !== menuId);
      setCachedData(`userMenus_${user.uid}`, updatedMenus);
      
      return true;
    } catch (error) {
      console.error('Error deleting menu:', error);
      return false;
    }
  }, [menus, setCachedData, user]);

  // Rename a menu
  const renameMenu = useCallback(async (menuId, newName) => {
    if (!user) return;
    
    try {
      const menuRef = doc(db, `users/${user.uid}/menus`, menuId);
      await setDoc(menuRef, { name: newName }, { merge: true });
      
      setMenus(prevMenus =>
        prevMenus.map(menu =>
          menu.id === menuId ? { ...menu, name: newName } : menu
        )
      );
      
      // Update cache
      const updatedMenus = menus.map(menu =>
        menu.id === menuId ? { ...menu, name: newName } : menu
      );
      setCachedData(`userMenus_${user.uid}`, updatedMenus);
      
      return true;
    } catch (error) {
      console.error('Error renaming menu:', error);
      return false;
    }
  }, [menus, setCachedData, user]);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadUserMenus();
    } else {
      setMenus([]);
      setLoading(false);
    }
  }, [user, loadUserMenus]);

  // Create the context value object
  const menuContextValue = {
    menus,
    loading,
    createMenu,
    addRecipeToMenu,
    removeRecipeFromMenu,
    deleteMenu,
    renameMenu,
    refreshMenus: loadUserMenus
  };
  
  // Provide the context value to children
  return (
    <MenuContext.Provider value={menuContextValue}>
      {children}
    </MenuContext.Provider>
  );
}; 